import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RotateCcw } from 'lucide-react'
import type { Label, Project } from '@witty-board/shared'
import { useUsersQuery } from '../../api/users.js'
import {
  useProjectsQuery,
  useDeletedProjectsQuery,
  useRestoreProjectMutation,
} from '../../api/projects.js'
import { useDeletedLabelsQuery, useRestoreLabelMutation } from '../../api/labels.js'
import { useDeletedTasksQuery } from '../../api/tasks.js'
import { formatDateTime } from '../../lib/datetime.js'
import { ProjectSwatch } from '../../components/ProjectSwatch.js'
import { DeletedTaskRow } from './DeletedTaskRow.js'

interface TrashSectionsProps {
  /** `undefined` = papelera global. Con proyecto, se limita a sus tareas y etiquetas. */
  projectId?: string
}

function Section({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-2">
      <h2 className="px-2 text-meta font-medium text-text-muted">
        {title} · {count}
      </h2>
      {count === 0 ? (
        <p className="px-2 text-meta text-text-muted">{t('trash.empty')}</p>
      ) : (
        <ul className="flex flex-col">{children}</ul>
      )}
    </section>
  )
}

function DeletedProjectRow({ project }: { project: Project }) {
  const { t, i18n } = useTranslation()
  const restore = useRestoreProjectMutation()

  return (
    <li className="flex h-10 max-md:h-11 items-center gap-3 rounded-card px-2 hover:bg-surface-hi">
      <ProjectSwatch color={project.color} />
      <span className="min-w-0 flex-1 truncate text-body text-text">{project.name}</span>
      <span className="shrink-0 text-meta text-text-muted">
        {project.deletedAt &&
          t('trash.deletedAt', { date: formatDateTime(project.deletedAt, i18n.language) })}
      </span>
      <button
        type="button"
        onClick={() => restore.mutate(project.id)}
        disabled={restore.isPending}
        className="flex h-8 max-md:h-11 shrink-0 items-center gap-1 rounded-card px-2 text-meta text-text-muted hover:bg-surface hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
      >
        <RotateCcw className="h-3 w-3" aria-hidden="true" />
        {t('trash.restore')}
      </button>
    </li>
  )
}

function DeletedLabelRow({ label, project }: { label: Label; project: Project | undefined }) {
  const { t, i18n } = useTranslation()
  const restore = useRestoreLabelMutation()

  return (
    <li className="flex h-10 max-md:h-11 items-center gap-3 rounded-card px-2 hover:bg-surface-hi">
      <span
        aria-hidden="true"
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: label.color }}
      />
      <span className="min-w-0 flex-1 truncate text-body text-text">
        {label.name}
        {project && <span className="ml-2 text-meta text-text-muted">{project.key}</span>}
      </span>
      <span className="shrink-0 text-meta text-text-muted">
        {label.deletedAt &&
          t('trash.deletedAt', { date: formatDateTime(label.deletedAt, i18n.language) })}
      </span>
      <button
        type="button"
        onClick={() => restore.mutate(label.id)}
        disabled={restore.isPending}
        className="flex h-8 max-md:h-11 shrink-0 items-center gap-1 rounded-card px-2 text-meta text-text-muted hover:bg-surface hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
      >
        <RotateCcw className="h-3 w-3" aria-hidden="true" />
        {t('trash.restore')}
      </button>
    </li>
  )
}

/** Contenido de la papelera (CLAUDE.md §6, fase 5), compartido entre la vista global y la de proyecto. */
export function TrashSections({ projectId }: TrashSectionsProps) {
  const { t } = useTranslation()
  const users = useUsersQuery()
  const projects = useProjectsQuery()
  const deletedProjects = useDeletedProjectsQuery()
  const deletedTasks = useDeletedTasksQuery(projectId)
  const deletedLabels = useDeletedLabelsQuery(projectId)

  const usersById = useMemo(
    () => new Map((users.data ?? []).map((u) => [u.id, u] as const)),
    [users.data],
  )
  const projectsById = useMemo(
    () => new Map((projects.data ?? []).map((p) => [p.id, p] as const)),
    [projects.data],
  )

  return (
    <div className="flex flex-col gap-6">
      {!projectId && (
        <Section title={t('trash.projectsSection')} count={deletedProjects.data?.length ?? 0}>
          {deletedProjects.data?.map((project) => (
            <DeletedProjectRow key={project.id} project={project} />
          ))}
        </Section>
      )}

      <Section title={t('trash.tasksSection')} count={deletedTasks.data?.length ?? 0}>
        {deletedTasks.data?.map((task) => (
          <DeletedTaskRow
            key={task.id}
            task={task}
            project={projectsById.get(task.projectId)}
            usersById={usersById}
            showProject={!projectId}
          />
        ))}
      </Section>

      <Section title={t('trash.labelsSection')} count={deletedLabels.data?.length ?? 0}>
        {deletedLabels.data?.map((label) => (
          <DeletedLabelRow
            key={label.id}
            label={label}
            project={label.projectId ? projectsById.get(label.projectId) : undefined}
          />
        ))}
      </Section>
    </div>
  )
}
